<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useListResource } from '@/composables/useListResource';
import { useOpenPdf } from '@/composables/useOpenPdf';
import { useSessionGuard } from '@/composables/useSessionGuard';
import UiScheduleCell from '@/components/ui/UiScheduleCell.vue';
import UiTermSelect from '@/components/ui/UiTermSelect.vue';
import { useTerm } from '@/composables/useTerm';
import { WEEKDAYS, formatTimeRange } from '@/utils/format';
import { schoolDayLabel, withSchoolDayRows } from '@/constants/schoolDay';

useSessionGuard();

const headers = [
  { title: 'Time', key: 'time', sortable: false },
  ...WEEKDAYS.map((day) => ({ title: day, key: day, sortable: false })),
];

const store = useStore();
const year = computed(() => store.getters.getFilterSelected);
const canPrintLoads = computed(() => store.state.user?.user?.isAdminApproved == 1);
const SERVER = process.env.VUE_APP_SERVER;

const teachers = shallowRef([]);
apiRequest('get', '/user-details/getAllVerifiedUser/TeachingRoleSched')
  .then((rows) => (teachers.value = rows || []))
  .catch(() => {});
const teacher = shallowRef(null);
// Each term has its own schedule.
const { hasTerms, terms, term, termQuery } = useTerm();

const { items, loading, error, load } = useListResource(() =>
  teacher.value
    ? apiRequest('get', `/enroll-student/getSpecificFacultySchedule/${year.value}/${teacher.value}?${termQuery.value}`)
    : [],
);
watch([teacher, year, term], load);
// Flag ceremony, breaks and lunch get their own rows between the classes.
const rows = computed(() => withSchoolDayRows(items.value));

const openPdf = useOpenPdf();
// The whole year: a year graded in terms prints a page per term.
function printSchedule() {
  openPdf(`/pdf-generator/getMySchedule/${teacher.value}/${year.value}`, 'Could not create Form 29.');
}
function printLoads() {
  window.open(`${SERVER}/pdf-generator/getAllUnderLoadFaculty/${year.value}?${termQuery.value}`, '_blank');
}
</script>

<template>
  <UiTableCard
    title="Faculty schedule"
    description="A teacher's weekly class schedule for the selected school year."
    :headers="headers"
    :items="rows"
    :band-label="schoolDayLabel"
    :loading="loading"
    :error="error"
    :searchable="false"
    :items-per-page="-1"
    :empty-text="teacher ? 'No classes scheduled.' : 'Choose a faculty member to see their schedule.'"
  >
    <template #actions>
      <v-btn v-if="canPrintLoads" variant="outlined" prepend-icon="mdi-clipboard-list-outline" @click="printLoads">
        Underload &amp; overload
      </v-btn>
      <v-btn color="primary" prepend-icon="mdi-printer-outline" :disabled="!teacher" @click="printSchedule">
        Print schedule
      </v-btn>
    </template>

    <template #toolbar>
      <v-autocomplete
        v-model="teacher"
        :items="teachers"
        item-title="name"
        item-value="id"
        placeholder="Choose a faculty member"
        prepend-inner-icon="mdi-account-tie-outline"
        hide-details
        style="max-width: 360px; width: 100%"
      />
      <UiTermSelect v-if="hasTerms" v-model="term" :items="terms" />
    </template>

    <template #[`item.time`]="{ item }">
      <span class="font-weight-medium text-no-wrap">{{ formatTimeRange(item.time) }}</span>
    </template>

    <template v-for="day in WEEKDAYS" :key="day" #[`item.${day}`]="{ item }">
      <UiScheduleCell :value="item[day]" />
    </template>
  </UiTableCard>
</template>

